import { supabase } from "../lib/supabase";

/**
 * DASHBOARD STATS
 */
export const getAdminStats = async () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayIso = today.toISOString();

  const [ordersTodayRes, ordersRes, productsRes, usersRes] = await Promise.all([
    supabase
      .from("orders")
      .select("id", { count: "exact", head: true })
      .gte("created_at", todayIso),

    supabase
      .from("orders")
      .select("total_amount"),

    supabase
      .from("products")
      .select("id", { count: "exact", head: true })
      .eq("available", true),

    supabase
      .from("profiles")
      .select("id", { count: "exact", head: true }),
  ]);

  if (ordersTodayRes.error) throw ordersTodayRes.error;
  if (ordersRes.error) throw ordersRes.error;
  if (productsRes.error) throw productsRes.error;
  if (usersRes.error) throw usersRes.error;

  const revenue = (ordersRes.data ?? []).reduce((sum, order) => {
    return sum + (Number(order.total_amount) || 0);
  }, 0);

  return {
    ordersToday: ordersTodayRes.count ?? 0,
    revenue,
    activeProducts: productsRes.count ?? 0,
    usersCount: usersRes.count ?? 0,
  };
};

/**
 * USERS
 */
export const getAdminUsers = async () => {
  const { data, error } = await supabase
    .from("profiles")
    .select("id, email, first_name, last_name, phone, role, is_active, created_at, updated_at")
    .order("created_at", { ascending: false });

  if (error) throw error;
  return data ?? [];
};

export const updateUserRole = async (userId, role) => {
  const { data, error } = await supabase
    .from("profiles")
    .update({ role, updated_at: new Date().toISOString() })
    .eq("id", userId)
    .select()
    .single();

  if (error) throw error;
  return data;
};

/**
 * PRODUCTS
 * ✅ Jointure sur categories via category_id
 */
export const getAdminProducts = async () => {
  const { data, error } = await supabase
    .from("products")
    .select(`
      id,
      name,
      slug,
      short_description,
      description,
      price,
      stock,
      available,
      featured,
      image_url,
      tags,
      category_id,
      categories (
        id,
        name,
        slug
      ),
      created_at,
      updated_at
    `)
    .order("created_at", { ascending: false });

  if (error) throw error;
  return data ?? [];
};

export const createProduct = async (payload) => {
  // On envoie category_id, pas category
  const { category, ...rest } = payload;
  const cleanPayload = {
    ...rest,
    category_id: payload.category_id || category || null,
  };

  const { data, error } = await supabase
    .from("products")
    .insert([cleanPayload])
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const updateProductById = async (productId, updates) => {
  // Nettoyer les champs non-colonnes avant l'update
  const { categories, category, ...cleanUpdates } = updates;

  const { data, error } = await supabase
    .from("products")
    .update({
      ...cleanUpdates,
      updated_at: new Date().toISOString(),
    })
    .eq("id", productId)
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const deleteProductById = async (productId) => {
  const { error } = await supabase
    .from("products")
    .delete()
    .eq("id", productId);

  if (error) throw error;
  return true;
};

/**
 * PRODUCT IMAGE UPLOAD
 */
export const uploadProductImage = async (file, productId) => {
  if (!file) return null;

  const fileExt = file.name.split(".").pop()?.toLowerCase() || "jpg";
  const fileName = `${productId}-${Date.now()}.${fileExt}`;
  const filePath = `products/${fileName}`;

  const { error: uploadError } = await supabase.storage
    .from("product-images")
    .upload(filePath, file, {
      cacheControl: "3600",
      upsert: true,
    });

  if (uploadError) throw uploadError;

  const { data } = supabase.storage
    .from("product-images")
    .getPublicUrl(filePath);

  return data.publicUrl;
};

/**
 * ORDERS
 */
export const getAdminOrders = async () => {
  const { data, error } = await supabase
    .from("orders")
    .select(`
      id,
      user_id,
      customer_first_name,
      customer_last_name,
      customer_email,
      customer_phone,
      status,
      payment_status,
      total_amount,
      stripe_session_id,
      pickup_type,
      notes,
      admin_notes,
      created_at,
      updated_at,
      order_items (
        id,
        product_id,
        product_name,
        unit_price,
        quantity,
        total_price,
        created_at
      )
    `)
    .order("created_at", { ascending: false });

  if (error) throw error;
  return data ?? [];
};

export const updateOrderStatus = async (orderId, status) => {
  const { data, error } = await supabase
    .from("orders")
    .update({ status, updated_at: new Date().toISOString() })
    .eq("id", orderId)
    .select()
    .single();

  if (error) throw error;
  return data;
};

/**
 * CATEGORIES
 */
export const getCategories = async () => {
  const { data, error } = await supabase
    .from("categories")
    .select("id, name, slug")
    .order("sort_order", { ascending: true });

  if (error) throw error;
  return data ?? [];
};

export const getNewsletterSubscribers = async () => {
  const { data, error } = await supabase
    .from('newsletter_subscribers')
    .select('id, email, first_name, subscribed, created_at')
    .order('created_at', { ascending: false })

  if (error) throw error
  return data ?? []
}

export const sendNewsletter = async ({ subject, html, preview_text }) => {
  const { data, error } = await supabase.functions.invoke('send-newsletter', {
    body: { subject, html, preview_text },
  })

  if (error) throw error
  return data
}